import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  mergeTranslations,
  coreTranslationsEn,
  coreTranslationsAr,
  coreTranslationsUr,
} from '@spcc/i18n';

// Import domain translations for Kiosk Portal
import {
  authTranslationEn,
  authTranslationAr,
  authTranslationUr,
} from '@spcc/domains/auth';
import {
  shipmentsTranslationEn,
  shipmentsTranslationAr,
  shipmentsTranslationUr,
} from '@spcc/domains/shipments';
import {
  weighbridgeOpsEn,
  weighbridgeOpsAr,
  weighbridgeOpsUr,
} from '@spcc/domains/weighbridge-ops';
import {
  queueEnTranslations,
  queueArTranslations,
} from '@spcc/domains/queue';
import {
  incoming_materialsTranslationEn,
  incoming_materialsTranslationAr,
  incoming_materialsTranslationUr,
} from '@spcc/domains/incoming-materials';
import { getStoredLanguage } from '@/lib/language-preferences';

/**
 * Kiosk Portal i18n Configuration
 *
 * BEFORE: 321 lines of duplicated translations
 * AFTER:  ~80 lines of clean domain aggregation (75% reduction!)
 *
 * This aggregates core translations with domain-specific translations
 * for weighbridge/station operations. Optimized for touch interfaces
 * and minimal, focused functionality.
 *
 * Domains included:
 * - auth: Simple authentication for kiosk users
 * - shipments: Shipment processing and weighing
 */

// Kiosk-specific translations (for weighbridge operations)
// These are unique to the kiosk portal and don't belong in a domain
const kioskSpecificEn = {
  scan: {
    title: 'Scan Order',
    subtitle: 'Enter the order number or scan barcode',
    inputLabel: 'Order Number',
    inputPlaceholder: 'Enter or scan order number (e.g., SP00XX)',
    submit: 'Scan Order',
    scanning: 'Processing...',
    assignResource: 'Assign Resource',
    resourceAssigned: 'Resource assigned successfully',
    success: 'Order scanned successfully',
    error: 'Failed to scan order',
    notFound: 'Order not found',
    logout: 'Logout',
    instructionsTitle: 'How to Scan',
    instruction1: '• Position barcode in front of scanner',
    instruction2: '• Or enter order number manually',
    instruction3: '• Press scan to load order details',
    orderDetails: 'Order Details',
    customer: 'Customer',
    scanAgain: 'Scan Again',
    changeResource: 'Change Resource',
    resourceAlreadyAssigned: 'Resource already assigned',
    resourceAlreadyAssignedDesc: 'You can continue to the next step',
    resourceMissing: 'Please enter driver & vehicle details',
    factoryMismatch:
      'This order belongs to a different factory. You cannot join the queue from this kiosk.',
    downloadingPDF: 'Downloading PDF...',
    downloadingPDFDesc: 'Generating PDF for order {{orderNumber}}',
    pdfDownloaded: 'PDF Downloaded',
    pdfDownloadedDesc: 'Order {{orderNumber}} PDF saved',
    pdfDownloadError: 'Download Failed',
    pdfDownloadErrorDesc: 'Could not download PDF',
    printingPDF: 'Sending to Printer...',
    printingPDFDesc: 'Preparing PDF for order {{orderNumber}}',
    pdfPrinted: 'Print Sent',
    pdfPrintedDesc: 'Order {{orderNumber}} sent to printer',
    pdfPrintError: 'Print Failed',
    pdfPrintErrorDesc: 'Could not print PDF',
    printingDeliveryNote: 'Sending Delivery Note...',
    printingDeliveryNoteDesc: 'Preparing delivery note for order {{orderNumber}}',
    deliveryNotePrinted: 'Delivery Note Sent',
    deliveryNotePrintedDesc: 'Delivery note for order {{orderNumber}} sent to printer',
    deliveryNotePrintError: 'Delivery Note Print Failed',
    deliveryNotePrintErrorDesc: 'Could not print the delivery note',
    orderLabel: 'Order',
    product: 'Product',
    quantity: 'Quantity',
    plant: 'Plant',
    notAssigned: 'Not assigned',
    statuses: {
      planned: 'Planned',
      processing: 'Processing',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      failed: 'Failed',
    },
    plantNames: {
      jazan: 'Jazan',
      bisha: 'Bisha',
      tihama: 'Tihama',
    },
    productNames: {
      paperBagCementTi: 'Tihama Cement - Paper Bags',
      bulkCementTi: 'Tihama Cement - Bulk',
      cementTi: 'Tihama Cement',
      ordinaryPortlandCement: 'Ordinary Portland Cement',
      sulfateResistantCement: 'Sulfate Resistant Cement',
      finishingCement: 'Finishing Cement',
      opc50kg: 'Ordinary Portland Cement - 50kg Bag',
      opcBulk: 'Ordinary Portland Cement - Bulk',
      src50kg: 'Sulfate Resistant Cement - 50kg Bag',
      srcBulk: 'Sulfate Resistant Cement - Bulk',
      fc50kg: 'Finishing Cement - 50kg Bag',
      fcBulk: 'Finishing Cement - Bulk',
    },
    driverAndVehicle: 'Driver & Vehicle',
    driver: 'Driver',
    vehicle: 'Vehicle',
    downloadPDF: 'Download PDF',
    printPDF: 'Print PDF',
    licensePlate: 'License Plate',
    capacity: 'Capacity',
    suggestions: 'Recent',
    driverName: 'Driver Name',
    driverNamePlaceholder: 'Name',
    driverPhone: 'Driver Phone',
    driverPhonePlaceholder: 'Phone',
    driverId: 'Driver ID',
    driverIdPlaceholder: 'ID Number',
    debugData: 'System Data',
    backendPayloadTitle: 'Payload Preview',
    incomplete: 'Incomplete',
    bags: 'Bags',
    tons: 'Tons',
    cameraPermissionTitle: 'Camera permission required',
    cameraPermissionDesc: 'Please allow camera access to scan QR codes.',
    cameraUnavailableTitle: 'Camera unavailable',
    cameraUnavailableDesc: 'Unable to access the camera. Please try again.',
    scanFailedTitle: 'No barcode detected',
    scanFailedDesc: 'Try again and make sure the QR code is clear.',
    scannerModuleTitle: 'Preparing scanner',
    scannerModuleDesc: 'Scanner is downloading. Please try again in a moment.',
    cameraUnsupportedTitle: 'Camera not supported',
    cameraUnsupportedDesc: 'This device does not support the camera scanner.',
    joiningQueue: 'Joining queue...',
    joiningQueueDesc: 'Please wait while we reserve your queue number.',
    registeringOrder: 'Registering order...',
    registeringOrderDesc:
      'Please wait while we register your truck at the gate.',
    alreadyRegisteredTitle: 'Already registered at the gate',
    alreadyRegisteredDesc:
      'Nothing was sent again. You can still change the truck and driver on this order.',
    cityRequiredTitle: 'Select order city',
    cityRequiredDesc:
      'This order is missing a city. Choose the order city before joining the queue.',
    cityRequiredAdsDesc:
      'This order is missing a city. Choose the order city to continue.',
    cityFieldLabel: 'City',
    cityFieldPlaceholder: 'Select a city',
    cityLoading: 'Loading cities...',
    cityLoadError: 'Failed to load cities. Please try scanning again.',
    cityEmpty: 'No cities are available.',
    citySelectionHint: 'The selected city will be saved with this order.',
    queueClosed: 'Queue is Currently Closed',
    queueClosedDesc: 'This plant is not accepting new queue entries at this time. Please check back later or contact your supervisor.',
    alreadyInQueueTitle: 'Already in the queue',
    queueTicketNumber: 'Ticket',
    queueStatusLabel: 'Status',
    queueTiming: {
      waiting: 'Waiting time',
      ready: 'Ready since',
      called: 'Called since',
      entered: 'In factory since',
      loading: 'Loading since',
    },
    queueElapsedMinutes_one: '{{count}} min',
    queueElapsedMinutes_other: '{{count}} min',
    queueElapsedHours: '{{hours}} h {{minutes}} min',
    queueStatus: {
      waiting: 'Waiting',
      ready: 'Ready',
      called: 'Called',
      entered: 'Entered',
      loading: 'Loading',
      completed: 'Completed',
      cancelled: 'Cancelled',
      expired: 'Expired',
      no_show: 'No show',
    },
  },
  header: {
    kiosk: 'Kiosk Operator',
    profile: 'Profile',
    settings: 'Settings',
    logout: 'Logout',
    guest: 'Guest',
    user: 'User',
    customerPortal: 'Purchase',
    ecommerce: 'Ecommerce',
  },
  home: {
    welcome: 'Welcome to South Gate',
    subtitle: 'Please select an option below to proceed with your operation.',
    existingCustomer: 'Existing Customer',
    existingCustomerDesc: 'Scan Order or Purchase using your account.',
    newCustomer: 'New Customer',
    newCustomerDesc: 'Open ecommerce, then continue.',
    scan: 'Scan Order',
    scanDesc: 'Scan your QR code or enter order number to begin weighing.',
    purchaseDesc:
      'Create new orders or manage your account via Customer Portal.',
    ecommerceDesc: 'Browse products and shop online.',
    inboundDelivery: 'Delivering to Us',
    inboundDeliveryDesc: 'Register your delivery if the gate could not read your QR or plate.',
  },
  privacy: {
    title: 'Privacy Policy',
    subtitle: 'Your privacy matters to us.',
    lastUpdated: 'Last updated: February 1, 2026',
    intro:
      'This Privacy Policy explains how the South Gate Kiosk Portal collects, uses, and protects information when you use this application.',
    link: 'Privacy Policy',
    backToLogin: 'Back to Login',
    sections: {
      overview: {
        title: 'Overview',
        body: 'We process information to operate the kiosk, provide services, and keep systems secure.',
      },
      collection: {
        title: 'Information We Collect',
        items: [
          'Account and identity details you provide (name, email, role).',
          'Order and shipment data you enter or scan.',
          'Device and browser data (IP address, device identifiers, logs).',
          'Usage data such as pages visited and actions taken.',
        ],
      },
      usage: {
        title: 'How We Use Information',
        items: [
          'Authenticate users and manage access.',
          'Process orders and deliveries.',
          'Improve performance, reliability, and support.',
          'Detect, prevent, and respond to security incidents.',
        ],
      },
      sharing: {
        title: 'When We Share Information',
        items: [
          'With service providers who support our operations (hosting, analytics).',
          "With your organization's administrators and authorized staff.",
          'When required by law or to protect safety and rights.',
        ],
      },
      retention: {
        title: 'Data Retention',
        body: 'We keep information only as long as needed for operational, legal, and audit requirements, then delete or anonymize it.',
      },
      security: {
        title: 'Security',
        body: 'We use technical and organizational safeguards to protect data.',
      },
      rights: {
        title: 'Your Rights',
        items: [
          'Access, update, or correct your personal data.',
          'Request deletion where permitted by law.',
          'Object to or restrict certain processing.',
        ],
      },
      cookies: {
        title: 'Cookies and Local Storage',
        body: 'We use essential cookies or local storage for session and language preferences.',
      },
      children: {
        title: "Children's Privacy",
        body: 'This service is intended for authorized business users and not for children.',
      },
      changes: {
        title: 'Changes to This Policy',
        body: 'We may update this policy and will revise the date above when changes are made.',
      },
      contact: {
        title: 'Contact Us',
        body: 'For questions, contact your organization administrator or South Gate support.',
      },
    },
  },
  common: {
    backToHome: 'Back to Home',
  },
  languagePrompt: {
    title: 'Select language',
    description: 'Select a language to continue.',
    expiredDescription:
      'Your session expired due to inactivity. Select a language to continue again.',
  },
  weighbridge: {
    title: 'Weighbridge',
    subtitle: 'Vehicle weighing operations',
    grossWeight: 'Gross Weight',
    tareWeight: 'Tare Weight',
    netWeight: 'Net Weight',
    weigh: 'Weigh Vehicle',
    weighing: 'Weighing...',
    complete: 'Complete Weighing',
    print: 'Print Ticket',
    vehicleDetails: 'Vehicle Details',
    driverInfo: 'Driver Information',
    materialType: 'Material Type',
    ticket: 'Weighbridge Ticket',
    startProcessing: 'Start Processing',
    startProcessingDesc:
      'Start processing by order number or vehicle license plate',
    firstWeigh: 'First Weigh',
    firstWeighDesc: 'Record empty vehicle weight (Tare)',
    secondWeigh: 'Second Weigh',
    secondWeighDesc: 'Record loaded vehicle weight (Gross)',
    markDelivered: 'Mark Delivered',
    markDeliveredDesc: 'Mark shipment as delivered at destination',
    orderNumber: 'Order Number',
    orderPlaceholder: 'Enter order number (e.g., SP00XX)',
    vehicleLicensePlate: 'Vehicle License Plate',
    vehicleLicensePlaceholder: 'Enter vehicle license plate',
    startProcessingSuccess: 'Processing started',
    startProcessingStatusBefore: 'Previous Status',
    startProcessingStatusAfter: 'Current Status',
    successFirstWeigh: 'First weigh recorded successfully',
    successSecondWeigh: 'Second weigh recorded successfully',
    successDelivered: 'Shipment marked as delivered',
    insufficientBalance: 'Insufficient Balance',
    balanceDetails: {
      chargeAmount: 'Estimated Charge',
      availableBalance: 'Available Balance',
      shortfall: 'Shortfall',
      hint: 'Please contact finance to increase credit limit or make a payment before proceeding.',
    },
    validation: {
      startProcessingOneRequired:
        'Please enter either order number or vehicle license plate',
      startProcessingOnlyOne:
        'Please enter only one: order number or vehicle license plate',
    },
  },
};

const kioskSpecificAr = {
  scan: {
    title: 'مسح الطلب',
    subtitle: 'أدخل رقم الطلب أو امسح الباركود',
    inputLabel: 'رقم الطلب',
    inputPlaceholder: 'أدخل أو امسح رقم الطلب (مثل: SP00XX)',
    submit: 'مسح الطلب',
    scanning: 'جاري المعالجة...',
    assignResource: 'تعيين المورد',
    resourceAssigned: 'تم تعيين المورد بنجاح',
    success: 'تم مسح الطلب بنجاح',
    error: 'فشل في مسح الطلب',
    notFound: 'الطلب غير موجود',
    logout: 'تسجيل الخروج',
    instructionsTitle: 'كيفية المسح',
    instruction1: '• ضع الباركود أمام الماسح',
    instruction2: '• أو أدخل رقم الطلب يدوياً',
    instruction3: '• اضغط مسح لتحميل تفاصيل الطلب',
    orderDetails: 'تفاصيل الطلب',
    customer: 'العميل',
    scanAgain: 'إعادة المسح',
    changeResource: 'تغيير المورد',
    resourceAlreadyAssigned: 'تم تعيين المورد بالفعل',
    resourceAlreadyAssignedDesc: 'يمكنك المتابعة للخطوة التالية',
    resourceMissing: 'الرجاء إدخال بيانات السائق والمركبة',
    factoryMismatch:
      'هذا الطلب يتبع مصنعاً مختلفاً. لا يمكنك الانضمام إلى الطابور من هذا الكشك.',
    downloadingPDF: 'جاري تحميل الملف...',
    downloadingPDFDesc: 'جاري إنشاء ملف للطلب {{orderNumber}}',
    pdfDownloaded: 'تم تحميل الملف',
    pdfDownloadedDesc: 'تم حفظ ملف الطلب {{orderNumber}}',
    pdfDownloadError: 'فشل التحميل',
    pdfDownloadErrorDesc: 'تعذر تحميل الملف',
    printingPDF: 'جاري الإرسال للطابعة...',
    printingPDFDesc: 'جاري تجهيز ملف الطلب {{orderNumber}}',
    pdfPrinted: 'تم الإرسال للطباعة',
    pdfPrintedDesc: 'تم إرسال الطلب {{orderNumber}} للطابعة',
    pdfPrintError: 'فشل الطباعة',
    pdfPrintErrorDesc: 'تعذر طباعة الملف',
    printingDeliveryNote: 'جاري إرسال إشعار التسليم...',
    printingDeliveryNoteDesc: 'جاري تجهيز إشعار التسليم للطلب {{orderNumber}}',
    deliveryNotePrinted: 'تم إرسال إشعار التسليم',
    deliveryNotePrintedDesc: 'تم إرسال إشعار التسليم للطلب {{orderNumber}} للطابعة',
    deliveryNotePrintError: 'فشل طباعة إشعار التسليم',
    deliveryNotePrintErrorDesc: 'تعذر طباعة إشعار التسليم',
    orderLabel: 'الطلب',
    product: 'المنتج',
    quantity: 'الكمية',
    plant: 'المصنع',
    notAssigned: 'غير محدد',
    statuses: {
      planned: 'مخطط',
      processing: 'قيد المعالجة',
      delivered: 'تم التسليم',
      cancelled: 'ملغي',
      failed: 'فشل',
    },
    plantNames: {
      jazan: 'جازان',
      bisha: 'بيشة',
      tihama: 'تهامة',
    },
    productNames: {
      paperBagCementTi: 'أسمنت تهامة - أكياس ورقية',
      bulkCementTi: 'أسمنت تهامة - سائب',
      cementTi: 'أسمنت تهامة',
      ordinaryPortlandCement: 'أسمنت بورتلاند عادي',
      sulfateResistantCement: 'أسمنت مقاوم للكبريتات',
      finishingCement: 'أسمنت التشطيب',
      opc50kg: 'أسمنت بورتلاند عادي - كيس 50 كجم',
      opcBulk: 'أسمنت بورتلاند عادي - سائب',
      src50kg: 'أسمنت مقاوم للكبريتات - كيس 50 كجم',
      srcBulk: 'أسمنت مقاوم للكبريتات - سائب',
      fc50kg: 'أسمنت التشطيب - كيس 50 كجم',
      fcBulk: 'أسمنت التشطيب - سائب',
    },
    driverAndVehicle: 'السائق والمركبة',
    driver: 'السائق',
    vehicle: 'المركبة',
    downloadPDF: 'تحميل PDF',
    printPDF: 'طباعة PDF',
    licensePlate: 'رقم اللوحة',
    capacity: 'السعة',
    suggestions: 'الأخيرة',
    driverName: 'اسم السائق',
    driverNamePlaceholder: 'الاسم',
    driverPhone: 'رقم الهاتف',
    driverPhonePlaceholder: 'الهاتف',
    driverId: 'رقم الهوية',
    driverIdPlaceholder: 'رقم الهوية',
    debugData: 'بيانات النظام',
    backendPayloadTitle: 'معاينة البيانات',
    incomplete: 'غير مكتمل',
    bags: 'أكياس',
    tons: 'طن',
    cameraPermissionTitle: 'يلزم إذن الكاميرا',
    cameraPermissionDesc: 'يرجى السماح بالوصول إلى الكاميرا لمسح رموز QR.',
    cameraUnavailableTitle: 'الكاميرا غير متاحة',
    cameraUnavailableDesc: 'تعذر الوصول إلى الكاميرا. حاول مرة أخرى.',
    scanFailedTitle: 'لم يتم اكتشاف رمز',
    scanFailedDesc: 'حاول مرة أخرى وتأكد من وضوح رمز QR.',
    scannerModuleTitle: 'جارٍ تجهيز الماسح',
    scannerModuleDesc: 'يتم تنزيل الماسح. يرجى المحاولة بعد قليل.',
    cameraUnsupportedTitle: 'الكاميرا غير مدعومة',
    cameraUnsupportedDesc: 'هذا الجهاز لا يدعم ماسح الكاميرا.',
    joiningQueue: 'جارٍ الانضمام إلى الطابور...',
    joiningQueueDesc: 'يرجى الانتظار بينما نحجز رقمك في الطابور.',
    registeringOrder: 'جارٍ تسجيل الطلب...',
    registeringOrderDesc: 'يرجى الانتظار بينما نسجّل شاحنتك لدى البوابة.',
    alreadyRegisteredTitle: 'مُسجَّل لدى البوابة بالفعل',
    alreadyRegisteredDesc:
      'لم يتم إرسال أي شيء مرة أخرى. لا يزال بإمكانك تغيير الشاحنة والسائق لهذا الطلب.',
    cityRequiredTitle: 'اختر مدينة الطلب',
    cityRequiredDesc: 'هذا الطلب لا يحتوي على مدينة. اختر مدينة الطلب قبل الانضمام إلى الطابور.',
    cityRequiredAdsDesc: 'هذا الطلب لا يحتوي على مدينة. اختر مدينة الطلب للمتابعة.',
    cityFieldLabel: 'المدينة',
    cityFieldPlaceholder: 'اختر مدينة',
    cityLoading: 'جارٍ تحميل المدن...',
    cityLoadError: 'فشل تحميل المدن. يرجى إعادة مسح الطلب.',
    cityEmpty: 'لا توجد مدن متاحة.',
    citySelectionHint: 'سيتم حفظ المدينة المختارة مع هذا الطلب.',
    queueClosed: 'الطابور مغلق حالياً',
    queueClosedDesc: 'هذا المصنع لا يقبل إدخالات طابور جديدة في الوقت الحالي. يرجى المحاولة لاحقاً أو الاتصال بالمشرف.',
    alreadyInQueueTitle: 'أنت بالفعل في الطابور',
    queueTicketNumber: 'رقم التذكرة',
    queueStatusLabel: 'الحالة',
    queueTiming: {
      waiting: 'مدة الانتظار',
      ready: 'جاهز منذ',
      called: 'تم النداء منذ',
      entered: 'داخل المصنع منذ',
      loading: 'جارٍ التحميل منذ',
    },
    queueElapsedMinutes_one: 'دقيقة واحدة',
    queueElapsedMinutes_other: '{{count}} دقيقة',
    queueElapsedHours: '{{hours}} س {{minutes}} د',
    queueStatus: {
      waiting: 'في الانتظار',
      ready: 'جاهز',
      called: 'تم النداء',
      entered: 'دخل',
      loading: 'جارٍ التحميل',
      completed: 'مكتمل',
      cancelled: 'ملغى',
      expired: 'منتهي',
      no_show: 'لم يحضر',
    },
  },
  header: {
    kiosk: 'مشغل جهاز الخدمة الذاتية',
    profile: 'الملف الشخصي',
    settings: 'الإعدادات',
    logout: 'تسجيل الخروج',
    guest: 'ضيف',
    user: 'مستخدم',
    customerPortal: 'شراء',
    ecommerce: 'المتجر الإلكتروني',
  },
  home: {
    welcome: 'أهلاً بك في بوابة الجنوب',
    subtitle: 'يرجى اختيار أحد الخيارات أدناه للمتابعة.',
    existingCustomer: 'عميل حالي',
    existingCustomerDesc: 'امسح الطلب أو اختر الشراء باستخدام حسابك.',
    newCustomer: 'عميل جديد',
    newCustomerDesc: 'افتح المتجر الإلكتروني ثم تابع.',
    scan: 'مسح الطلب',
    scanDesc: 'امسح رمز QR أو أدخل رقم الطلب لبدء عملية الوزن.',
    purchaseDesc: 'إنشاء طلبات جديدة أو إدارة حسابك عبر بوابة العملاء.',
    ecommerceDesc: 'تصفح المنتجات وتسوق عبر الإنترنت.',
    inboundDelivery: 'توصيل لنا',
    inboundDeliveryDesc: 'سجّل توصيلتك إذا تعذّر على البوابة قراءة رمز QR أو رقم اللوحة.',
  },
  privacy: {
    title: 'سياسة الخصوصية',
    subtitle: 'خصوصيتك تهمنا.',
    lastUpdated: 'آخر تحديث: 1 فبراير 2026',
    intro:
      'توضح سياسة الخصوصية هذه كيف تجمع بوابة الجنوب لجهاز الخدمة الذاتية المعلومات وتستخدمها وتحميها عند استخدامك للتطبيق.',
    link: 'سياسة الخصوصية',
    backToLogin: 'العودة لتسجيل الدخول',
    sections: {
      overview: {
        title: 'نظرة عامة',
        body: 'نقوم بمعالجة المعلومات لتشغيل جهاز الخدمة الذاتية وتقديم الخدمات والحفاظ على أمان الأنظمة.',
      },
      collection: {
        title: 'المعلومات التي نجمعها',
        items: [
          'بيانات الحساب والهوية التي تقدمها (الاسم، البريد الإلكتروني، الدور).',
          'بيانات الطلبات والشحنات التي تدخلها أو تمسحها.',
          'بيانات الجهاز والمتصفح (عنوان IP ومعرفات الجهاز والسجلات).',
          'بيانات الاستخدام مثل الصفحات التي تزورها والإجراءات التي تقوم بها.',
        ],
      },
      usage: {
        title: 'كيف نستخدم المعلومات',
        items: [
          'التحقق من الهوية وإدارة الوصول.',
          'معالجة الطلبات وعمليات التسليم.',
          'تحسين الأداء والاعتمادية والدعم.',
          'اكتشاف الحوادث الأمنية ومنعها والاستجابة لها.',
        ],
      },
      sharing: {
        title: 'متى نشارك المعلومات',
        items: [
          'مع مزودي الخدمات الذين يدعمون تشغيلنا (الاستضافة، التحليلات).',
          'مع مسؤولي مؤسستك والموظفين المخولين.',
          'عندما يطلب القانون ذلك أو لحماية السلامة والحقوق.',
        ],
      },
      retention: {
        title: 'الاحتفاظ بالبيانات',
        body: 'نحتفظ بالمعلومات فقط للمدة اللازمة لأغراض التشغيل والمتطلبات القانونية والتدقيق، ثم نحذفها أو نُجهّلها.',
      },
      security: {
        title: 'الأمان',
        body: 'نستخدم ضوابط فنية وتنظيمية لحماية البيانات.',
      },
      rights: {
        title: 'حقوقك',
        items: [
          'الوصول إلى بياناتك الشخصية أو تحديثها أو تصحيحها.',
          'طلب الحذف عندما يسمح القانون.',
          'الاعتراض على بعض المعالجة أو تقييدها.',
        ],
      },
      cookies: {
        title: 'ملفات تعريف الارتباط والتخزين المحلي',
        body: 'نستخدم ملفات تعريف الارتباط الأساسية أو التخزين المحلي للجلسة وتفضيلات اللغة.',
      },
      children: {
        title: 'خصوصية الأطفال',
        body: 'هذه الخدمة مخصصة للمستخدمين المصرح لهم لأغراض العمل وليست للأطفال.',
      },
      changes: {
        title: 'التغييرات على هذه السياسة',
        body: 'قد نقوم بتحديث هذه السياسة وسنقوم بتعديل تاريخ التحديث أعلاه عند إجراء أي تغييرات.',
      },
      contact: {
        title: 'تواصل معنا',
        body: 'للاستفسارات، تواصل مع مسؤول مؤسستك أو دعم بوابة الجنوب.',
      },
    },
  },
  common: {
    backToHome: 'العودة للرئيسية',
  },
  languagePrompt: {
    title: 'اختر اللغة',
    description: 'اختر اللغة للمتابعة.',
    expiredDescription:
      'انتهت الجلسة بسبب عدم النشاط. اختر اللغة للمتابعة مرة أخرى.',
  },
  weighbridge: {
    title: 'الميزان',
    subtitle: 'عمليات وزن المركبات',
    grossWeight: 'الوزن الإجمالي',
    tareWeight: 'وزن المركبة فارغة',
    netWeight: 'الوزن الصافي',
    weigh: 'وزن المركبة',
    weighing: 'جاري الوزن...',
    complete: 'إكمال الوزن',
    print: 'طباعة التذكرة',
    vehicleDetails: 'تفاصيل المركبة',
    driverInfo: 'معلومات السائق',
    materialType: 'نوع المادة',
    ticket: 'تذكرة الميزان',
    startProcessing: 'بدء المعالجة',
    startProcessingDesc: 'ابدأ المعالجة برقم الطلب أو لوحة المركبة',
    firstWeigh: 'الوزنة الأولى',
    firstWeighDesc: 'تسجيل وزن المركبة فارغة',
    secondWeigh: 'الوزنة الثانية',
    secondWeighDesc: 'تسجيل وزن المركبة محملة',
    markDelivered: 'تأكيد التسليم',
    markDeliveredDesc: 'تحديد الشحنة كمسلّمة في الوجهة',
    orderNumber: 'رقم الطلب',
    orderPlaceholder: 'أدخل رقم الطلب (مثل: SP00XX)',
    vehicleLicensePlate: 'لوحة المركبة',
    vehicleLicensePlaceholder: 'أدخل رقم لوحة المركبة',
    startProcessingSuccess: 'تم بدء المعالجة',
    startProcessingStatusBefore: 'الحالة السابقة',
    startProcessingStatusAfter: 'الحالة الحالية',
    successFirstWeigh: 'تم تسجيل الوزنة الأولى بنجاح',
    successSecondWeigh: 'تم تسجيل الوزنة الثانية بنجاح',
    successDelivered: 'تم تحديد الشحنة كمسلّمة',
    insufficientBalance: 'رصيد غير كافٍ',
    balanceDetails: {
      chargeAmount: 'المبلغ المتوقع',
      availableBalance: 'الرصيد المتاح',
      shortfall: 'العجز',
      hint: 'يرجى التواصل مع المالية لزيادة حد الائتمان أو إجراء دفعة قبل المتابعة.',
    },
    validation: {
      startProcessingOneRequired: 'يرجى إدخال رقم الطلب أو لوحة المركبة',
      startProcessingOnlyOne: 'يرجى إدخال واحد فقط: رقم الطلب أو لوحة المركبة',
    },
  },
};

const kioskSpecificUr = {
  scan: {
    title: 'آرڈر اسکین کریں',
    subtitle: 'آرڈر نمبر درج کریں یا بارکوڈ اسکین کریں',
    inputLabel: 'آرڈر نمبر',
    inputPlaceholder: 'آرڈر نمبر درج کریں یا اسکین کریں (مثلاً: SP00XX)',
    submit: 'آرڈر اسکین کریں',
    scanning: 'پروسیسنگ...',
    assignResource: 'وسیلہ تفویض کریں',
    resourceAssigned: 'وسیلہ کامیابی سے تفویض ہوا',
    success: 'آرڈر کامیابی سے اسکین ہو گیا',
    error: 'آرڈر اسکین نہیں ہو سکا',
    notFound: 'آرڈر نہیں ملا',
    logout: 'لاگ آؤٹ',
    instructionsTitle: 'اسکین کرنے کا طریقہ',
    instruction1: '• بارکوڈ کو اسکینر کے سامنے رکھیں',
    instruction2: '• یا آرڈر نمبر دستی طور پر درج کریں',
    instruction3: '• آرڈر کی تفصیلات لوڈ کرنے کے لیے اسکین دبائیں',
    orderDetails: 'آرڈر کی تفصیلات',
    customer: 'گاہک',
    scanAgain: 'دوبارہ اسکین کریں',
    changeResource: 'وسیلہ تبدیل کریں',
    resourceAlreadyAssigned: 'وسیلہ پہلے ہی تفویض ہے',
    resourceAlreadyAssignedDesc: 'آپ اگلے مرحلے پر جا سکتے ہیں',
    resourceMissing: 'براہِ کرم ڈرائیور اور گاڑی کی تفصیلات درج کریں',
    factoryMismatch:
      'یہ آرڈر مختلف فیکٹری سے تعلق رکھتا ہے۔ آپ اس کیوسک سے قطار میں شامل نہیں ہو سکتے۔',
    downloadingPDF: 'PDF ڈاؤن لوڈ ہو رہی ہے...',
    downloadingPDFDesc: 'آرڈر {{orderNumber}} کیلئے PDF تیار ہو رہی ہے',
    pdfDownloaded: 'PDF ڈاؤن لوڈ ہو گئی',
    pdfDownloadedDesc: 'آرڈر {{orderNumber}} کی PDF محفوظ ہو گئی',
    pdfDownloadError: 'ڈاؤن لوڈ ناکام',
    pdfDownloadErrorDesc: 'PDF ڈاؤن لوڈ نہیں ہو سکی',
    printingPDF: 'پرنٹر کو بھیجا جا رہا ہے...',
    printingPDFDesc: 'آرڈر {{orderNumber}} کیلئے PDF تیار ہو رہی ہے',
    pdfPrinted: 'پرنٹ بھیجا گیا',
    pdfPrintedDesc: 'آرڈر {{orderNumber}} پرنٹر کو بھیجا گیا',
    pdfPrintError: 'پرنٹ ناکام',
    pdfPrintErrorDesc: 'PDF پرنٹ نہیں ہو سکی',
    printingDeliveryNote: 'ڈیلیوری نوٹ بھیجا جا رہا ہے...',
    printingDeliveryNoteDesc: 'آرڈر {{orderNumber}} کا ڈیلیوری نوٹ تیار ہو رہا ہے',
    deliveryNotePrinted: 'ڈیلیوری نوٹ بھیجا گیا',
    deliveryNotePrintedDesc: 'آرڈر {{orderNumber}} کا ڈیلیوری نوٹ پرنٹر کو بھیجا گیا',
    deliveryNotePrintError: 'ڈیلیوری نوٹ پرنٹ ناکام',
    deliveryNotePrintErrorDesc: 'ڈیلیوری نوٹ پرنٹ نہیں ہو سکا',
    orderLabel: 'آرڈر',
    product: 'مصنوع',
    quantity: 'مقدار',
    plant: 'پلانٹ',
    notAssigned: 'تفویض نہیں ہوا',
    statuses: {
      planned: 'منصوبہ بند',
      processing: 'پروسیسنگ',
      delivered: 'ترسیل شدہ',
      cancelled: 'منسوخ',
      failed: 'ناکام',
    },
    plantNames: {
      jazan: 'جازان',
      bisha: 'بیشہ',
      tihama: 'تہامہ',
    },
    productNames: {
      paperBagCementTi: 'تہامہ سیمنٹ - کاغذی تھیلے',
      bulkCementTi: 'تہامہ سیمنٹ - بلک',
      cementTi: 'تہامہ سیمنٹ',
      ordinaryPortlandCement: 'عام پورٹ لینڈ سیمنٹ',
      sulfateResistantCement: 'سلفیٹ مزاحم سیمنٹ',
      finishingCement: 'فنِشنگ سیمنٹ',
      opc50kg: 'عام پورٹ لینڈ سیمنٹ - 50 کلو تھیلا',
      opcBulk: 'عام پورٹ لینڈ سیمنٹ - بلک',
      src50kg: 'سلفیٹ مزاحم سیمنٹ - 50 کلو تھیلا',
      srcBulk: 'سلفیٹ مزاحم سیمنٹ - بلک',
      fc50kg: 'فنِشنگ سیمنٹ - 50 کلو تھیلا',
      fcBulk: 'فنِشنگ سیمنٹ - بلک',
    },
    driverAndVehicle: 'ڈرائیور اور گاڑی',
    driver: 'ڈرائیور',
    vehicle: 'گاڑی',
    downloadPDF: 'PDF ڈاؤن لوڈ',
    printPDF: 'PDF پرنٹ',
    licensePlate: 'نمبر پلیٹ',
    capacity: 'گنجائش',
    suggestions: 'حالیہ',
    driverName: 'ڈرائیور کا نام',
    driverNamePlaceholder: 'نام',
    driverPhone: 'ڈرائیور فون',
    driverPhonePlaceholder: 'فون',
    driverId: 'ڈرائیور آئی ڈی',
    driverIdPlaceholder: 'آئی ڈی نمبر',
    debugData: 'سسٹم ڈیٹا',
    backendPayloadTitle: 'ڈیٹا کا پیش نظارہ',
    incomplete: 'نامکمل',
    bags: 'بیگ',
    tons: 'ٹن',
    cameraPermissionTitle: 'کیمرا کی اجازت درکار ہے',
    cameraPermissionDesc: 'QR کوڈ اسکین کرنے کے لیے کیمرا کی اجازت دیں۔',
    cameraUnavailableTitle: 'کیمرا دستیاب نہیں',
    cameraUnavailableDesc: 'کیمرا تک رسائی نہیں ہو سکی۔ دوبارہ کوشش کریں۔',
    scanFailedTitle: 'بارکوڈ نہیں ملا',
    scanFailedDesc: 'دوبارہ کوشش کریں اور QR کوڈ واضح رکھیں۔',
    scannerModuleTitle: 'اسکینر تیار ہو رہا ہے',
    scannerModuleDesc:
      'اسکینر ڈاؤن لوڈ ہو رہا ہے۔ تھوڑی دیر بعد دوبارہ کوشش کریں۔',
    cameraUnsupportedTitle: 'کیمرا سپورٹ نہیں کرتا',
    cameraUnsupportedDesc: 'یہ ڈیوائس کیمرا اسکینر کو سپورٹ نہیں کرتی۔',
    joiningQueue: 'قطار میں شامل کیا جا رہا ہے...',
    joiningQueueDesc:
      'براہِ کرم انتظار کریں، آپ کا قطار نمبر محفوظ کیا جا رہا ہے۔',
    registeringOrder: 'آرڈر رجسٹر کیا جا رہا ہے...',
    registeringOrderDesc:
      'براہِ کرم انتظار کریں، آپ کا ٹرک گیٹ پر رجسٹر کیا جا رہا ہے۔',
    alreadyRegisteredTitle: 'پہلے ہی گیٹ پر رجسٹر ہے',
    alreadyRegisteredDesc:
      'دوبارہ کچھ نہیں بھیجا گیا۔ آپ اب بھی اس آرڈر کا ٹرک اور ڈرائیور تبدیل کر سکتے ہیں۔',
    cityRequiredTitle: 'آرڈر کا شہر منتخب کریں',
    cityRequiredDesc:
      'اس آرڈر میں شہر موجود نہیں ہے۔ قطار میں شامل ہونے سے پہلے آرڈر کا شہر منتخب کریں۔',
    cityRequiredAdsDesc:
      'اس آرڈر میں شہر موجود نہیں ہے۔ جاری رکھنے کے لیے آرڈر کا شہر منتخب کریں۔',
    cityFieldLabel: 'شہر',
    cityFieldPlaceholder: 'ایک شہر منتخب کریں',
    cityLoading: 'شہر لوڈ ہو رہے ہیں...',
    cityLoadError: 'شہر لوڈ نہیں ہو سکے۔ براہ کرم آرڈر دوبارہ اسکین کریں۔',
    cityEmpty: 'کوئی شہر دستیاب نہیں۔',
    citySelectionHint: 'منتخب کیا گیا شہر اس آرڈر کے ساتھ محفوظ ہو جائے گا۔',
    queueClosed: 'قطار ابھی بند ہے',
    queueClosedDesc: 'یہ پلانٹ ابھی نئے قطار اندراجات قبول نہیں کر رہا۔ بعد میں دوبارہ کوشش کریں یا سپروائزر سے رابطہ کریں۔',
    alreadyInQueueTitle: 'آپ پہلے ہی قطار میں ہیں',
    queueTicketNumber: 'ٹکٹ',
    queueStatusLabel: 'حالت',
    queueTiming: {
      waiting: 'انتظار کا دورانیہ',
      ready: 'تیار، تب سے',
      called: 'بلایا گیا، تب سے',
      entered: 'فیکٹری میں، تب سے',
      loading: 'لوڈنگ، تب سے',
    },
    queueElapsedMinutes_one: '{{count}} منٹ',
    queueElapsedMinutes_other: '{{count}} منٹ',
    queueElapsedHours: '{{hours}} گھنٹے {{minutes}} منٹ',
    queueStatus: {
      waiting: 'انتظار میں',
      ready: 'تیار',
      called: 'بلایا گیا',
      entered: 'داخل',
      loading: 'لوڈنگ',
      completed: 'مکمل',
      cancelled: 'منسوخ',
      expired: 'میعاد ختم',
      no_show: 'حاضر نہیں',
    },
  },
  header: {
    kiosk: 'کِیوسک آپریٹر',
    profile: 'پروفائل',
    settings: 'سیٹنگز',
    logout: 'لاگ آؤٹ',
    guest: 'مہمان',
    user: 'صارف',
    customerPortal: 'خریداری',
    ecommerce: 'ای کامرس',
  },
  home: {
    welcome: 'بوابة الجنوب میں خوش آمدید',
    subtitle: 'براہِ کرم آگے بڑھنے کے لیے نیچے سے کوئی آپشن منتخب کریں۔',
    existingCustomer: 'موجودہ صارف',
    existingCustomerDesc: 'اسکین آرڈر کریں یا اپنے اکاؤنٹ سے خرید کریں۔',
    newCustomer: 'نیا صارف',
    newCustomerDesc: 'ای کامرس کھولیں، پھر جاری رکھیں۔',
    scan: 'آرڈر اسکین کریں',
    scanDesc: 'QR کوڈ اسکین کریں یا آرڈر نمبر درج کر کے وزن کا عمل شروع کریں۔',
    purchaseDesc:
      'کسٹمر پورٹل کے ذریعے نئے آرڈر بنائیں یا اپنا اکاؤنٹ منیج کریں۔',
    ecommerceDesc: 'مصنوعات دیکھیں اور آن لائن خریداری کریں۔',
    inboundDelivery: 'ہمیں ڈیلیوری کریں',
    inboundDeliveryDesc: 'اگر گیٹ آپ کا QR یا نمبر پلیٹ نہ پڑھ سکے تو اپنی ڈیلیوری رجسٹر کریں۔',
  },
  privacy: {
    title: 'پرائیویسی پالیسی',
    subtitle: 'آپ کی رازداری ہمارے لیے اہم ہے۔',
    lastUpdated: 'آخری تازہ کاری: 1 فروری 2026',
    intro:
      'یہ پرائیویسی پالیسی بتاتی ہے کہ بوابة الجنوب کِیوسک پورٹل آپ کی معلومات کیسے جمع، استعمال اور محفوظ کرتا ہے۔',
    link: 'پرائیویسی پالیسی',
    backToLogin: 'لاگ اِن پر واپس',
    sections: {
      overview: {
        title: 'جائزہ',
        body: 'ہم معلومات کو کِیوسک چلانے، خدمات فراہم کرنے اور سسٹم کو محفوظ رکھنے کے لیے استعمال کرتے ہیں۔',
      },
      collection: {
        title: 'ہم کون سی معلومات جمع کرتے ہیں',
        items: [
          'آپ کی فراہم کردہ اکاؤنٹ اور شناختی تفصیلات (نام، ای میل، کردار).',
          'آپ کے درج یا اسکین کردہ آرڈر اور شپمنٹ ڈیٹا.',
          'ڈیوائس اور براؤزر کی معلومات (آئی پی ایڈریس، ڈیوائس شناختی نمبر، لاگز).',
          'استعمال کی معلومات جیسے دیکھی گئی صفحات اور کیے گئے اقدامات.',
        ],
      },
      usage: {
        title: 'ہم معلومات کیسے استعمال کرتے ہیں',
        items: [
          'صارفین کی تصدیق اور رسائی کا نظم.',
          'آرڈرز اور ڈیلیوری پراسیس کرنا.',
          'کارکردگی، بھروسہ مندی اور سپورٹ کو بہتر بنانا.',
          'سیکیورٹی واقعات کی شناخت، روک تھام اور جواب.',
        ],
      },
      sharing: {
        title: 'ہم معلومات کب شیئر کرتے ہیں',
        items: [
          'ان سروس پرووائیڈرز کے ساتھ جو آپریشن میں مدد دیتے ہیں (ہوسٹنگ، اینالٹکس).',
          'آپ کی تنظیم کے منتظمین اور مجاز عملے کے ساتھ.',
          'قانونی تقاضوں یا حفاظت و حقوق کے لیے.',
        ],
      },
      retention: {
        title: 'ڈیٹا محفوظ رکھنے کی مدت',
        body: 'ہم معلومات کو صرف اتنی مدت تک رکھتے ہیں جتنی آپریشن، قانونی اور آڈٹ تقاضوں کے لیے ضروری ہو، پھر اسے حذف یا گمنام کرتے ہیں۔',
      },
      security: {
        title: 'سیکیورٹی',
        body: 'ہم ڈیٹا کی حفاظت کے لیے تکنیکی اور تنظیمی اقدامات کرتے ہیں۔',
      },
      rights: {
        title: 'آپ کے حقوق',
        items: [
          'اپنے ذاتی ڈیٹا تک رسائی، اپ ڈیٹ یا درستگی.',
          'قانونی طور پر ممکن ہو تو حذف کی درخواست.',
          'کچھ پروسیسنگ پر اعتراض یا اسے محدود کرنا.',
        ],
      },
      cookies: {
        title: 'کوکیز اور لوکل اسٹوریج',
        body: 'ہم سیشن اور زبان کی ترجیحات کے لیے ضروری کوکیز یا لوکل اسٹوریج استعمال کرتے ہیں۔',
      },
      children: {
        title: 'بچوں کی رازداری',
        body: 'یہ سروس کاروباری مقاصد کے لیے مجاز صارفین کے لیے ہے اور بچوں کے لیے نہیں۔',
      },
      changes: {
        title: 'اس پالیسی میں تبدیلیاں',
        body: 'ہم اس پالیسی کو وقتاً فوقتاً اپ ڈیٹ کر سکتے ہیں اور اوپر دی گئی تاریخ کو تبدیل کریں گے۔',
      },
      contact: {
        title: 'رابطہ',
        body: 'سوالات کے لیے اپنی تنظیم کے منتظم یا بوابة الجنوب سپورٹ سے رابطہ کریں۔',
      },
    },
  },
  common: {
    backToHome: 'ہوم پر واپس',
  },
  languagePrompt: {
    title: 'زبان منتخب کریں',
    description: 'جاری رکھنے کے لیے زبان منتخب کریں۔',
    expiredDescription:
      'غیرفعال رہنے کی وجہ سے سیشن ختم ہو گیا۔ دوبارہ جاری رکھنے کے لیے زبان منتخب کریں۔',
  },
  weighbridge: {
    title: 'ترازو',
    subtitle: 'گاڑی وزن کرنے کے عمل',
    grossWeight: 'مجموعی وزن',
    tareWeight: 'خالی وزن',
    netWeight: 'خالص وزن',
    weigh: 'گاڑی وزن کریں',
    weighing: 'وزن ہو رہا ہے...',
    complete: 'وزن مکمل کریں',
    print: 'ٹکٹ پرنٹ کریں',
    vehicleDetails: 'گاڑی کی تفصیلات',
    driverInfo: 'ڈرائیور معلومات',
    materialType: 'مواد کی قسم',
    ticket: 'ترازو ٹکٹ',
    startProcessing: 'پروسیسنگ شروع کریں',
    startProcessingDesc: 'آرڈر نمبر یا گاڑی نمبر پلیٹ سے پروسیسنگ شروع کریں',
    firstWeigh: 'پہلا وزن',
    firstWeighDesc: 'خالی گاڑی کا وزن درج کریں',
    secondWeigh: 'دوسرا وزن',
    secondWeighDesc: 'بھری گاڑی کا وزن درج کریں',
    markDelivered: 'ڈیلیوری مکمل',
    markDeliveredDesc: 'شپمنٹ کو منزل پر پہنچا ہوا نشان زد کریں',
    orderNumber: 'آرڈر نمبر',
    orderPlaceholder: 'آرڈر نمبر درج کریں (مثال: SP00XX)',
    vehicleLicensePlate: 'گاڑی نمبر پلیٹ',
    vehicleLicensePlaceholder: 'گاڑی نمبر پلیٹ درج کریں',
    startProcessingSuccess: 'پروسیسنگ شروع ہو گئی',
    startProcessingStatusBefore: 'پچھلی حالت',
    startProcessingStatusAfter: 'موجودہ حالت',
    successFirstWeigh: 'پہلا وزن کامیابی سے درج ہو گیا',
    successSecondWeigh: 'دوسرا وزن کامیابی سے درج ہو گیا',
    successDelivered: 'شپمنٹ ڈیلیور ہو گئی',
    insufficientBalance: 'ناکافی بیلنس',
    balanceDetails: {
      chargeAmount: 'متوقع چارج',
      availableBalance: 'دستیاب بیلنس',
      shortfall: 'کمی',
      hint: 'براہِ کرم آگے بڑھنے سے پہلے مالیاتی محکمے سے رابطہ کریں۔',
    },
    validation: {
      startProcessingOneRequired:
        'براہِ کرم آرڈر نمبر یا گاڑی نمبر پلیٹ درج کریں',
      startProcessingOnlyOne:
        'براہِ کرم صرف ایک درج کریں: آرڈر نمبر یا گاڑی نمبر پلیٹ',
    },
  },
};

const kioskAuthTranslationEn = {
  ...authTranslationEn,
  loginSubtitle: 'Sign in to your South Gate Kiosk Portal',
};

const kioskAuthTranslationAr = {
  ...authTranslationAr,
  loginSubtitle:
    'سجّل الدخول إلى بوابة الجنوب لجهاز الخدمة الذاتية',
};

const kioskAuthTranslationUr = {
  ...authTranslationUr,
  loginSubtitle: 'بوابة الجنوب کیوسک پورٹل میں سائن اِن کریں',
};

// English translations - using domain-first architecture + kiosk-specific
const enTranslations = mergeTranslations(coreTranslationsEn, {
  auth: kioskAuthTranslationEn,
  shipments: shipmentsTranslationEn,
  incomingMaterials: incoming_materialsTranslationEn,
  ...queueEnTranslations,
  ...weighbridgeOpsEn,
  ...kioskSpecificEn,
});

// Arabic translations - using domain-first architecture + kiosk-specific
const arTranslations = mergeTranslations(coreTranslationsAr, {
  auth: kioskAuthTranslationAr,
  shipments: shipmentsTranslationAr,
  incomingMaterials: incoming_materialsTranslationAr,
  ...queueArTranslations,
  ...weighbridgeOpsAr,
  ...kioskSpecificAr,
});

// Urdu translations
const urTranslations = mergeTranslations(coreTranslationsUr, {
  auth: kioskAuthTranslationUr,
  shipments: shipmentsTranslationUr,
  incomingMaterials: incoming_materialsTranslationUr,
  ...weighbridgeOpsUr,
  ...kioskSpecificUr,
});

// i18next resources
const resources = {
  en: {
    translation: enTranslations,
  },
  ar: {
    translation: arTranslations,
  },
  ur: {
    translation: urTranslations,
  },
};

const initialLang = getStoredLanguage() ?? 'en';

function setDocumentLanguage(language: string) {
  if (typeof document === 'undefined') return;
  const dir = language === 'ar' || language === 'ur' ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', language);
}

i18n.use(initReactI18next).init({
  resources,
  lng: initialLang,
  fallbackLng: 'en',
  debug: process.env.NODE_ENV === 'development',

  interpolation: {
    escapeValue: false,
  },

  react: {
    useSuspense: false,
  },
});

setDocumentLanguage(initialLang);
i18n.on('languageChanged', (language) => {
  setDocumentLanguage(language);
});

export default i18n;

/**
 * ✅ Type-Safe Usage in Components:
 *
 * import { useTranslation } from 'react-i18next'
 *
 * function MyComponent() {
 *   const { t } = useTranslation()
 *
 *   return (
 *     <div>
 *       <h1>{t('scan.title')}</h1>
 *       <button>{t('common.save')}</button>
 *       <span>{t('weighbridge.grossWeight')}</span>
 *       <p>{t('shipments.status.pending')}</p>
 *     </div>
 *   )
 * }
 *
 * 🎯 Benefits:
 * - Type safety with autocomplete
 * - Zero duplication across portals
 * - Touch-optimized for kiosk interfaces
 * - Weighbridge-specific functionality
 */
